import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Icon, TypeAhead } from '@gumtree/ui-library';
import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';

import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';
import { locationFieldAriaLabel, locationFieldPlaceholderText } from '../constants';
import { trackClickSearchBar } from '../search-bar-actions';
import type { ShellUseDispatch } from '../../../reducers/common';

interface LocationFieldProps {
    setLocationAndUpdateSuggestions(value: string): void;
    loadRecentLocationHistory(): void;
    query: string;
    selectSuggestion(option: { name: string }, useRecentHistory: boolean): void;
    suggestions: any[];
    useRecentHistory: boolean;
    removeSingleFromHistory: (text: string) => void;
    clearAllFromHistory: () => void;
}

const LocationField: React.FC<LocationFieldProps> = ({
    setLocationAndUpdateSuggestions,
    loadRecentLocationHistory,
    query,
    selectSuggestion,
    suggestions = [],
    useRecentHistory,
    removeSingleFromHistory,
    clearAllFromHistory,
}) => {
    const dispatch = useDispatch() as ShellUseDispatch;
    const [hasTrackedLocationHistoryShown, setHasTrackedLocationHistoryShown] = useState(false);

    const debounceRef = useRef<number | undefined>(undefined);
    const DEBOUNCE_MS = 250;

    const handleChangeOptimised = useCallback(
        (value: string) => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
            debounceRef.current = window.setTimeout(() => {
                setLocationAndUpdateSuggestions(value);
            }, DEBOUNCE_MS);
        },
        [setLocationAndUpdateSuggestions]
    );

    useEffect(
        () => () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        },
        []
    );

    useEffect(() => {
        loadRecentLocationHistory();
    }, []);

    const inputProps = {
        'aria-label': locationFieldAriaLabel,
        placeholder: locationFieldPlaceholderText,
        ...qaAttribute('search-location-field'),
    };

    const handleFocus = useCallback(() => {
        window.setTimeout(() => {
            dispatch(trackClickSearchBar('location', suggestions.length));
        }, 0);
    }, [dispatch, suggestions.length]);

    const handleOpen = useCallback(() => {
        if (useRecentHistory && !hasTrackedLocationHistoryShown) {
            window.setTimeout(() => {
                trackV2('LocationHistoryShown');
                setHasTrackedLocationHistoryShown(true);
            }, 0);
        }
    }, [useRecentHistory, hasTrackedLocationHistoryShown]);

    return (
        <TypeAhead
            inputProps={inputProps}
            icon={<Icon type="beacon" size="medium" />}
            id="header-search-location"
            limit={10}
            name="search_location"
            onFocus={handleFocus}
            onOpen={handleOpen}
            onSelectOption={(option) => {
                selectSuggestion(option, useRecentHistory);
            }}
            onChange={handleChangeOptimised}
            options={suggestions}
            removeSingleFromHistory={removeSingleFromHistory}
            clearAllFromHistory={clearAllFromHistory}
            useRecentHistory={useRecentHistory}
            defaultValue={query}
            typeAheadIcon={
                useRecentHistory ? (
                    <Icon type="clock" size="medium" />
                ) : (
                    <Icon type="beacon" size="medium" />
                )
            }
            titleOfRecentHistory={`Recent Location${suggestions.length === 1 ? '' : 's'}`}
            showClearAllButton={false}
        />
    );
};

export default LocationField;
